/**
 * IndexedDB store for offline time entries.
 * Uses idb for promise-based access.
 *
 * Schema:
 * - timeEntries: Pending time entries to sync when online
 * - syncMeta: Metadata about sync state
 * - cachedTasks: Cached task list for offline viewing
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * Custom error class for IndexedDB errors.
 * Provides structured error information for debugging and user feedback.
 */
export class IndexedDBError extends Error {
  constructor(
    public operation: string,
    message: string,
    public originalError?: unknown
  ) {
    super(`IndexedDB ${operation} failed: ${message}`);
    this.name = 'IndexedDBError';
  }
}

/**
 * Wrap an IndexedDB operation with error handling.
 */
async function withErrorHandling<T>(
  operation: string,
  fn: () => Promise<T>
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`IndexedDB ${operation} failed:`, error);

    // Check for specific error types
    if (message.includes('QuotaExceededError')) {
      throw new IndexedDBError(operation, 'Storage quota exceeded. Please free up space.', error);
    }
    if (message.includes('InvalidStateError')) {
      throw new IndexedDBError(operation, 'Database is closing or closed.', error);
    }
    if (message.includes('VersionError')) {
      throw new IndexedDBError(operation, 'Database version conflict. Please refresh the page.', error);
    }

    throw new IndexedDBError(operation, message, error);
  }
}

// Types for offline storage
export interface OfflineTimeEntry {
  clientEntryId: string; // UUID generated on device
  taskId: string;
  startTime: string; // ISO timestamp
  endTime: string | null;
  deviceTimeOffsetSeconds: number;
  createdAt: string; // When entry was created locally
  syncStatus: 'pending' | 'syncing' | 'synced' | 'error';
  syncError?: string;
  lastSyncAttempt?: string;
}

export interface CachedTask {
  id: string;
  partNumber: string;
  description: string;
  laborBucket: string;
  projectName: string;
  gpSalesOrderNumber?: string;
  priority: number;
  estimatedMinutes: number;
  actualMinutes: number;
  estimatedMinutesMin: number;
  estimatedMinutesMax: number;
  status: string;
  scheduledStart: string | null;
  cachedAt: string;
}

interface SchedulerWorkerDB extends DBSchema {
  timeEntries: {
    key: string; // clientEntryId
    value: OfflineTimeEntry;
    indexes: {
      'by-sync-status': string;
      'by-task': string;
    };
  };
  syncMeta: {
    key: string;
    value: {
      key: string;
      value: string | number | boolean;
      updatedAt: string;
    };
  };
  cachedTasks: {
    key: string; // task id
    value: CachedTask;
    indexes: {
      'by-status': string;
    };
  };
}

const DB_NAME = 'scheduler-worker';
const DB_VERSION = 2;

let dbInstance: IDBPDatabase<SchedulerWorkerDB> | null = null;

/**
 * Get or create the database instance.
 */
export async function getDB(): Promise<IDBPDatabase<SchedulerWorkerDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  return withErrorHandling('open', async () => {
    dbInstance = await openDB<SchedulerWorkerDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Time entries store (from version 1)
        if (oldVersion < 1) {
          const timeEntryStore = db.createObjectStore('timeEntries', {
            keyPath: 'clientEntryId',
          });
          timeEntryStore.createIndex('by-sync-status', 'syncStatus');
          timeEntryStore.createIndex('by-task', 'taskId');

          db.createObjectStore('syncMeta', { keyPath: 'key' });
        }

        // Cached tasks store (from version 2)
        if (oldVersion < 2) {
          const cachedTaskStore = db.createObjectStore('cachedTasks', {
            keyPath: 'id',
          });
          cachedTaskStore.createIndex('by-status', 'status');
        }
      },
      blocked() {
        console.warn('IndexedDB upgrade blocked. Please close other tabs.');
      },
      blocking() {
        // Close the connection when a new version is requested
        dbInstance?.close();
        dbInstance = null;
      },
      terminated() {
        // Database connection was unexpectedly terminated
        console.error('IndexedDB connection terminated unexpectedly.');
        dbInstance = null;
      },
    });

    return dbInstance;
  });
}

/**
 * Add a new offline time entry.
 */
export async function addOfflineTimeEntry(
  entry: Omit<OfflineTimeEntry, 'syncStatus' | 'createdAt'>
): Promise<void> {
  return withErrorHandling('addTimeEntry', async () => {
    const db = await getDB();
    await db.put('timeEntries', {
      ...entry,
      syncStatus: 'pending',
      createdAt: new Date().toISOString(),
    });
  });
}

/**
 * Get all pending time entries that need syncing.
 */
export async function getPendingTimeEntries(): Promise<OfflineTimeEntry[]> {
  return withErrorHandling('getPendingEntries', async () => {
    const db = await getDB();
    return db.getAllFromIndex('timeEntries', 'by-sync-status', 'pending');
  });
}

/**
 * Update sync status for a time entry.
 */
export async function updateTimeEntrySyncStatus(
  clientEntryId: string,
  status: OfflineTimeEntry['syncStatus'],
  error?: string
): Promise<void> {
  return withErrorHandling('updateSyncStatus', async () => {
    const db = await getDB();
    const entry = await db.get('timeEntries', clientEntryId);
    if (entry) {
      await db.put('timeEntries', {
        ...entry,
        syncStatus: status,
        syncError: error,
        lastSyncAttempt: new Date().toISOString(),
      });
    }
  });
}

/**
 * Get count of unsynced entries (pending + syncing) for UI indicator.
 * Includes 'syncing' entries because they may be stranded from a failed sync attempt.
 */
export async function getPendingCount(): Promise<number> {
  return withErrorHandling('getPendingCount', async () => {
    const db = await getDB();
    const pending = await db.countFromIndex('timeEntries', 'by-sync-status', 'pending');
    const syncing = await db.countFromIndex('timeEntries', 'by-sync-status', 'syncing');
    return pending + syncing;
  });
}

/**
 * Clear synced entries (cleanup).
 */
export async function clearSyncedEntries(): Promise<void> {
  return withErrorHandling('clearSyncedEntries', async () => {
    const db = await getDB();
    const synced = await db.getAllFromIndex('timeEntries', 'by-sync-status', 'synced');
    const tx = db.transaction('timeEntries', 'readwrite');
    await Promise.all(synced.map((entry) => tx.store.delete(entry.clientEntryId)));
    await tx.done;
  });
}

/**
 * Cache task list for offline use.
 */
export async function cacheTaskList(tasks: Array<Omit<CachedTask, 'cachedAt'>>): Promise<void> {
  return withErrorHandling('cacheTaskList', async () => {
    const db = await getDB();
    const tx = db.transaction('cachedTasks', 'readwrite');
    const cachedAt = new Date().toISOString();

    // Clear existing and add new
    await tx.store.clear();
    await Promise.all(
      tasks.map((task) =>
        tx.store.put({ ...task, cachedAt })
      )
    );
    await tx.done;
  });
}

/**
 * Get cached task list.
 */
export async function getCachedTaskList(): Promise<CachedTask[]> {
  return withErrorHandling('getCachedTaskList', async () => {
    const db = await getDB();
    return db.getAll('cachedTasks');
  });
}

/**
 * Clear cached tasks.
 */
export async function clearCachedTasks(): Promise<void> {
  return withErrorHandling('clearCachedTasks', async () => {
    const db = await getDB();
    const tx = db.transaction('cachedTasks', 'readwrite');
    await tx.store.clear();
    await tx.done;
  });
}
